import { formatDay } from "utils/helpers";
import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "react-i18next";

interface SingleRegisterDirectiveDetailProps {
    directive: any;
    fileRowProps?: any;
    onUpdate?: (updated: any) => void;
}

export default function SingleRegisterDirectiveDetail({
    directive,
    fileRowProps,
    onUpdate
}: SingleRegisterDirectiveDetailProps) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col bg-white rounded-lg shadow-md px-8 p-6 mb-4">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h2 className="font-semibold text-3xl text-[#212121] mb-1">
                        {t("RequestDetail")}
                    </h2>
                </div>
            </div>

            <div className="flex gap-1 text-[#9D0303] font-medium text-sm mb-1">
                <p>{formatDay(directive.request_created_date || directive.updated_at || directive.created_at)?.weekday || ""},</p>
                <p>{formatDay(directive.request_created_date || directive.updated_at || directive.created_at)?.rest || ""}</p>
                {directive.written_comments_last_updated && (
                    <span className="ml-2 text-xs text-gray-500">
                        | {t("WrittenComments")}: {new Date(directive.written_comments_last_updated).toLocaleDateString()}
                    </span>
                )}
            </div>

            {/* Reference Number */}
            <div className="mt-4 mb-6">
                <div className="space-y-4">
                    <div>
                        <h3 className="font-semibold text-lg mb-2">{t("RegNumber")}</h3>
                        <p className="text-[#606060]">
                            {directive.registry_number || directive.application_number || directive.request_letter_reference_number || "-"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Summary of Comments */}
            {directive.summary_of_comments && (
                <div className="mt-5 mb-3 rounded-lg p-4 bg-gray-5">
                    <h3 className="font-semibold text-lg mb-2">{t("SummaryOfComments")}</h3>
                    <div className="space-y-2">
                        {directive.summary_of_comments.split('\n').map(
                            (paragraph: string, index: number) =>
                                paragraph.trim() !== "" && (
                                    <p
                                        key={index}
                                        className="font-light text-base text-[#212121] leading-relaxed"
                                    >
                                        {paragraph}
                                    </p>
                                )
                        )}
                    </div>
                </div>
            )}



            {/* Written Comments Section - Latest File Only */}
            {directive.documents && directive.documents.filter((doc: any) => doc.document_type === "dir_reg_written_comments").length > 0 && (
                <div className="mt-5 mb-3 rounded-lg p-4 bg-gray-5">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-lg">{t("WrittenComments")}</h3>
                        {directive.written_comments_last_updated && (
                            <span className="text-xs text-gray-500">
                                Last updated: {new Date(directive.written_comments_last_updated).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                    {(() => {
                        const writtenComments = directive.documents
                            .filter((doc: any) => doc.document_type === "dir_reg_written_comments")
                            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                        const latestComment = writtenComments[0];
                        const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://research-api.oagb.et/api/";
                        const documentUrl = `${baseUrl}directive_registration/directive-additional-info-documents/document/${latestComment.document_uuid}/`;
                        const extension = latestComment.document_path?.split('.').pop()?.toLowerCase() || '';
                        const isPdf = extension === 'pdf';
                        
                        return (
                            <div className="flex flex-col gap-3 p-3 border border-gray-300 rounded-lg">
                                <p className="text-sm text-gray-900 font-medium">
                                    {latestComment.document_path?.split('/').pop() || 'written-comments'}
                                </p>
                                {isPdf ? (
                                    <iframe
                                        src={documentUrl}
                                        width="100%"
                                        height="500px"
                                        className="border border-gray-300 rounded"
                                        title="Written Comments PDF"
                                    />
                                ) : (
                                    <a 
                                        href={documentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                                    >
                                        Download Written Comments Document
                                    </a>
                                )}
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}
